import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import SideView from "../../../../../components/Generic/SideView";
import { ViewTab } from "../../../../../components/Generic/SideView/types";
import { InvoicesCharges } from "../../../../../models/invoice.charges";
import useAction from "../hooks/useAction";
import InvoiceDetails from "./components/Details";
import InvoiceViewSkeleton from "./components/Details/skeleton";
import InvoiceOrders from "./components/Orders";
import { InvoiceTabs } from "./utils";

interface Props {}

export interface InvoiceViewHandles {
  open: (invoiceId: number) => void;
  close: () => void;
}

const InvoiceViewComponent: ForwardRefRenderFunction<InvoiceViewHandles, Props> = (_, ref) => {
  const { t: translate } = useTranslation();
  const { getInvoiceView } = useAction();

  const [keyId, setKeyId] = useState<number>();
  const [isSidebarOpened, setIsSidebarOpened] = useState<boolean>(false);

  const { data: invoice = null, isFetching } = getInvoiceView(Number(keyId));
  const isFetchingOrders = false;
  const onOpenSidebar = useCallback(
    (dispatchId: number) => {
      // eslint-disable-next-line eqeqeq
      if (keyId != dispatchId) setKeyId(dispatchId);

      setIsSidebarOpened(true);
    },
    [keyId]
  );

  const onCloseSidebar = useCallback(() => {
    setIsSidebarOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenSidebar,
    close: onCloseSidebar,
  }));

  const sidebarTitle = useMemo(() => {
    let title = translate("invoice");
    const reference = invoice?.id;

    if (reference) title = `${title} #${reference}`;

    return title;
  }, [invoice]);

  const purchaseOrders = useMemo(() => {
    return invoice?.invoiceShipment?.poNumber?.split(", ").map((orderNumber) => {
      return {
        reference: orderNumber,
        supplierName: invoice?.invoiceShipment?.supplierName,
        origin: invoice?.invoiceShipment?.origin,
        destination: invoice?.invoiceShipment?.destination,
        eta: invoice?.invoiceShipment?.eta,
        etd: invoice?.invoiceShipment?.etd,
        mode: invoice?.invoiceShipment?.mode,
      };
    });
  }, [invoice?.invoiceShipment]);

  const tabs = useMemo(() => {
    const tabs: Array<ViewTab> = [
      {
        key: InvoiceTabs.DETAILS,
        label: translate("details"),
        loading: isFetching,
        isEmpty: false,
        isOpened: false,
        render: () => <InvoiceDetails invoice={invoice as InvoicesCharges} />,
        renderLoading: () => <InvoiceViewSkeleton />,
      },
      {
        key: InvoiceTabs.PURCHASE_ORDERS,
        label: translate("purchaseOrders"),
        loading: false,
        isEmpty: false,
        isOpened: false,
        render: () => <InvoiceOrders orders={purchaseOrders ?? []} />,
        // render: () => <InvoiceOrders orders={[]} />,
        renderLoading: () => <InvoiceViewSkeleton />,
      },
    ];

    return tabs;
  }, [invoice]);

  return (
    <SideView
      isLoading={isFetching || isFetchingOrders}
      isOpened={isSidebarOpened}
      onClose={onCloseSidebar}
      tabs={tabs}
      defaultTab={InvoiceTabs.DETAILS}
      title={sidebarTitle}
      buttonsComponent={[]}
    />
  );
};

export default forwardRef(InvoiceViewComponent);
